import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

interface HoverTooltipProps {
  content: string;
  children: React.ReactNode;
  wrapperClassName?: string;
}

const HoverTooltip: React.FC<HoverTooltipProps> = ({ content, children, wrapperClassName }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number; above: boolean } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible || !anchorRef.current) return;

    const updatePosition = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const above = spaceAbove >= 32 || rect.top < window.innerHeight / 2;
      setPosition({
        left: rect.left + rect.width / 2,
        top: above ? rect.top : rect.bottom,
        above,
      });
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible]);

  return (
    <>
      <div
        ref={anchorRef}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => setVisible(false)}
        className={wrapperClassName ?? 'inline-flex'}
      >
        {children}
      </div>
      {visible && position &&
        ReactDOM.createPortal(
          <div
            className="fixed z-[9999] pointer-events-none px-3 py-2 max-w-xs bg-gray-900 text-white text-xs rounded-lg shadow-lg"
            style={{
              left: position.left,
              top: position.above ? position.top - 8 : position.top + 8,
              transform: `translate(-50%, ${position.above ? '-100%' : '0'})`,
            }}
            role="tooltip"
          >
            {content}
          </div>,
          document.body
        )}
    </>
  );
};

export default HoverTooltip;
